// Single source of truth for reading teaching-quality ratings off a
// quality_assessments row.
//
// An org can define its own rubric (organizations.quality_rubric), so the
// dimension set is no longer fixed. New assessments write the full result to
// the `dimensions` JSONB column; the five legacy `<key>_rating` columns are
// still written for the built-in keys only. Reading through here means a page
// works for a custom rubric and for rows written before rubric support.

export const LEGACY_DIMENSION_KEYS = [
  'clarity',
  'engagement',
  'adaptability',
  'encouragement',
  'structure',
];

export const LEGACY_DIMENSION_LABELS = {
  clarity: 'Clarity',
  engagement: 'Engagement',
  adaptability: 'Adaptability',
  encouragement: 'Encouragement',
  structure: 'Structure',
};

const VALID_RATINGS = ['strong', 'adequate', 'needs_development'];

export const normaliseRating = (r) => {
  if (typeof r !== 'string') return null;
  const v = r.trim().toLowerCase();
  return VALID_RATINGS.includes(v) ? v : null;
};

// strong=3, adequate=2, needs_development=1, unknown=0. Kept identical to the
// copies it replaces so existing percentages don't shift.
export const ratingToScore = (r) => {
  switch (normaliseRating(r)) {
    case 'strong': return 3;
    case 'adequate': return 2;
    case 'needs_development': return 1;
    default: return 0;
  }
};

export const humaniseKey = (key) =>
  String(key || '')
    .replace(/[_-]+/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase())
    .trim();

// The dimension list a page should render for an org: the org's rubric when it
// has one, otherwise the five built-ins. Returns [{ key, label }].
export const resolveRubricDimensions = (quality_rubric) => {
  let rubric = quality_rubric;
  if (typeof rubric === 'string') {
    try { rubric = JSON.parse(rubric); } catch { rubric = null; }
  }
  if (rubric && !Array.isArray(rubric) && Array.isArray(rubric.dimensions)) {
    rubric = rubric.dimensions;
  }
  if (Array.isArray(rubric)) {
    const out = [];
    const seen = new Set();
    for (const d of rubric) {
      const key = typeof d?.key === 'string' ? d.key.trim() : '';
      if (!key || seen.has(key)) continue;
      seen.add(key);
      out.push({ key, label: (typeof d?.label === 'string' && d.label.trim()) || humaniseKey(key) });
      if (out.length === 5) break;
    }
    if (out.length > 0) return out;
  }
  return LEGACY_DIMENSION_KEYS.map((key) => ({ key, label: LEGACY_DIMENSION_LABELS[key] }));
};

// key -> rating for one assessment row. Prefers the `dimensions` JSONB and
// falls back to the legacy flat columns, so it is correct either way.
export const getDimensionRatings = (qa) => {
  const out = {};
  if (!qa) return out;

  let dims = qa.dimensions;
  if (typeof dims === 'string') {
    try { dims = JSON.parse(dims); } catch { dims = null; }
  }
  if (Array.isArray(dims) && dims.length > 0) {
    let found = false;
    for (const d of dims) {
      const key = typeof d?.key === 'string' ? d.key.trim() : '';
      if (!key) continue;
      out[key] = normaliseRating(d.rating);
      found = true;
    }
    if (found) return out;
  }

  for (const key of LEGACY_DIMENSION_KEYS) {
    const val = normaliseRating(qa[`${key}_rating`]);
    if (val !== null) out[key] = val;
  }
  return out;
};

// The columns a page must select to read ratings through this helper.
export const QUALITY_SELECT_COLUMNS =
  'dimensions, clarity_rating, engagement_rating, adaptability_rating, encouragement_rating, structure_rating';
